import { MapPin, Clock, Users } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Badge } from "@/components/ui/badge";
import type { Setting } from "@/types/summary";
import { SettingCard } from "./SettingCard";

interface SettingSubcardProps {
  setting: Setting;
  index: number;
}

export function SettingSubcard({ setting, index }: SettingSubcardProps) {
  const { t } = useTranslation("jobDetails");
  const [isExpanded, setIsExpanded] = useState(false);
  const isPrimary = setting.setting_type === "primary";

  const accentBarClassName = isPrimary
    ? "bg-primary-400 opacity-40"
    : "bg-secondary-300";

  const badgeClassName = isPrimary
    ? "bg-primary-25 text-primary-500"
    : "bg-secondary-50 text-secondary-500";

  return (
    <SettingCard
      accentBarClassName={accentBarClassName}
      headerContent={
        <>
          <Badge
            className={`text-[0.6875rem] leading-4 font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full border-transparent ${badgeClassName}`}
            data-testid={`setting-badge-${setting.setting_type}`}
          >
            {isPrimary
              ? t("worldAndSettingCard.primary")
              : t("worldAndSettingCard.major")}
          </Badge>
          <span className="text-[0.8125rem] font-medium text-secondary-500">
            {setting.location_label}
          </span>
          <span className="text-secondary-500">·</span>
          <span className="text-[0.8125rem] font-medium text-secondary-500">
            {setting.time_period_label}
          </span>
        </>
      }
      preview={setting.location}
      expandedContent={
        <>
          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5 text-primary-500" />
              <span className="text-[0.75rem] font-semibold uppercase tracking-wide text-secondary-500">
                {t("worldAndSettingCard.location")}
              </span>
            </div>
            <p className="text-[0.875rem] leading-5 text-secondary-500 pl-5">
              {setting.location}
            </p>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-primary-500" />
              <span className="text-[0.75rem] font-semibold uppercase tracking-wide text-secondary-500">
                {t("worldAndSettingCard.timePeriod")}
              </span>
            </div>
            <p className="text-[0.875rem] leading-5 text-secondary-500 pl-5">
              {setting.time_period}
            </p>
          </div>

          <div className="space-y-1.5">
            <div className="flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5 text-primary-500" />
              <span className="text-[0.75rem] font-semibold uppercase tracking-wide text-secondary-500">
                {t("worldAndSettingCard.socialStructure")}
              </span>
            </div>
            <p className="text-[0.875rem] leading-5 text-secondary-500 pl-5">
              {setting.local_social_structure}
            </p>
          </div>
        </>
      }
      isExpanded={isExpanded}
      onToggle={() => setIsExpanded(!isExpanded)}
      testIds={{
        accentBar: `setting-accent-bar-${index}`,
        toggle: `setting-toggle-${index}`,
        chevronRight: `setting-chevron-right-${index}`,
        chevronDown: `setting-chevron-down-${index}`,
        preview: `setting-preview-${index}`,
        content: `setting-content-${index}`,
      }}
    />
  );
}
